import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Trash2, Ship, Layers, ArrowLeft, Move,
  Square, Music, Wine, Bath, TreePalm, Tag, Type, Minus, Crown, Anchor,
  Grid3x3, Maximize2, BoxSelect, Users, AlertTriangle
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import RealTable from "../components/RealTable";
import { useFloorFit } from "../hooks/useFloorFit";

const API = window.location.origin;

// Style C — Dark Slate Operations Board zone palette (TableOperations ile birebir)
const ZONES = [
  { value: "cam_kenari", label: "Cam Kenarı", color: "#06B6D4", bg: "rgba(6,182,212,0.15)", glow: "0 0 15px rgba(6,182,212,0.35)", badgeBg: "#164E63", badgeText: "#A5F3FC" },
  { value: "sahne_onu", label: "Sahne Önü", color: "#F97316", bg: "rgba(249,115,22,0.15)", glow: "0 0 15px rgba(249,115,22,0.35)", badgeBg: "#7C2D12", badgeText: "#FFEDD5" },
  { value: "vip_lounge", label: "VIP Lounge", color: "#F59E0B", bg: "rgba(245,158,11,0.15)", glow: "0 0 15px rgba(245,158,11,0.35)", badgeBg: "#78350F", badgeText: "#FDE68A" },
  { value: "standart", label: "Standart", color: "#64748B", bg: "rgba(100,116,139,0.15)", glow: "none", badgeBg: "#334155", badgeText: "#E2E8F0" },
];
const FLOOR_TYPES = ["STANDART", "VIP"];

const ELEMENT_TYPES = [
  { type: "sahne", label: "Sahne", icon: Music, defaultW: 200, defaultH: 80 },
  { type: "bar", label: "Bar", icon: Wine, defaultW: 150, defaultH: 50 },
  { type: "wc", label: "WC", icon: Bath, defaultW: 60, defaultH: 50 },
  { type: "plant", label: "Bitki", icon: TreePalm, defaultW: 30, defaultH: 30 },
  { type: "zone_label", label: "Bölge Etiketi", icon: Tag, defaultW: 140, defaultH: 30 },
  { type: "border", label: "Sınır Çizgisi", icon: Minus, defaultW: 200, defaultH: 4 },
  { type: "text", label: "Metin", icon: Type, defaultW: 120, defaultH: 30 },
];

const getZoneStyle = (zone) => ZONES.find(z => z.value === zone) || ZONES[3];
const getElementDef = (type) => ELEMENT_TYPES.find(e => e.type === type) || ELEMENT_TYPES[0];

export default function TableSchemaEditor() {
  const [boats, setBoats] = useState([]);
  const [floors, setFloors] = useState([]);
  const [tables, setTables] = useState([]);
  const [elements, setElements] = useState([]);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addFloorOpen, setAddFloorOpen] = useState(false);
  const [newFloor, setNewFloor] = useState({ name: "", floor_type: "STANDART" });
  const [toolMode, setToolMode] = useState(null); // null | "table" | element_type
  const [newTableForm, setNewTableForm] = useState({ number: "", zone: "standart", capacity: 4 });
  const [newElementLabel, setNewElementLabel] = useState("");
  const [selected, setSelected] = useState(null); // {type: "table"|"element", id}
  const [snap, setSnap] = useState(true);
  const [lanes, setLanes] = useState([]);
  const [newLane, setNewLane] = useState({ name: "", zone: "cam_kenari", capacity: 20, dynamic: false });
  const [arrangeOpt, setArrangeOpt] = useState({ mode: "auto", rotate: false, reverse: false }); // bölge masalarını dizme seçenekleri
  const [drawRect, setDrawRect] = useState(null); // {x,y,w,h} — çizim önizleme
  const drawRef = useRef(null);
  const canvasRef = useRef(null);
  const GRID = 20;
  const WLOGICAL = 1560, HLOGICAL = 1900;
  const snapVal = (v) => (snap ? Math.round(v / GRID) * GRID : Math.round(v));

  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [boatsRes, floorsRes, tablesRes, elementsRes] = await Promise.all([
        axios.get(`${API}/api/tm/boats`, { headers }).then(r => r.data?.boats || []).catch(() => []),
        axios.get(`${API}/api/tm/floors`, { headers }),
        axios.get(`${API}/api/tm/tables`, { headers }),
        axios.get(`${API}/api/tm/elements`, { headers }),
      ]);
      setBoats(Array.isArray(boatsRes) ? boatsRes : []);
      setFloors(floorsRes.data?.floors || []);
      setTables(tablesRes.data?.tables || []);
      setElements(elementsRes.data?.elements || []);
      if (!selectedBoat && boatsRes.length > 0) setSelectedBoat(boatsRes[0].id);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(true); }, [fetchData]);

  const fetchLanes = useCallback(async (floorId) => {
    if (!floorId) { setLanes([]); return; }
    try {
      const r = await axios.get(`${API}/api/tm/lanes/${floorId}`, { headers });
      setLanes(r.data?.lanes || []);
    } catch { setLanes([]); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLanes(selectedFloor); }, [selectedFloor, fetchLanes]);

  const reloadFloor = async () => {
    const [tablesRes] = await Promise.all([axios.get(`${API}/api/tm/tables`, { headers })]);
    setTables(tablesRes.data?.tables || []);
    fetchLanes(selectedFloor);
  };

  const addLaneRect = async (rect) => {
    const gx = snapVal(rect.x), gy = snapVal(rect.y);
    const gw = Math.max(120, snapVal(rect.w)), gh = Math.max(90, snapVal(rect.h));
    try {
      const r = await axios.post(`${API}/api/tm/lanes`, {
        floor_id: selectedFloor, name: newLane.name.trim() || "Bölge",
        zone: newLane.zone, dynamic: newLane.dynamic, capacity: newLane.capacity,
        x: gx, y: gy, width: gw, height: gh,
      }, { headers });
      setNewLane(p => ({ ...p, name: "" }));
      await reloadFloor();
      if (r.data?.lane) setSelected({ type: "lane", id: r.data.lane.id });
      setToolMode(null);
      toast.success("Bölge oluşturuldu");
    } catch (err) { toast.error(err.response?.data?.detail || "Bölge oluşturulamadı"); }
  };

  const updateLane = async (id, data) => {
    setLanes(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));
    try { await axios.put(`${API}/api/tm/lanes/${id}`, data, { headers }); await reloadFloor(); } catch { toast.error("Güncellenemedi"); }
  };

  const deleteLane = async (id) => {
    if (!window.confirm("Bu şeridi ve içindeki masaları silmek istediğinize emin misiniz?")) return;
    setLanes(prev => prev.filter(l => l.id !== id));
    setSelected(null);
    try { await axios.delete(`${API}/api/tm/lanes/${id}`, { headers }); await reloadFloor(); } catch { toast.error("Silinemedi"); }
  };

  const setLaneTables = async (id, fours, twos) => {
    const caps = [...Array(Math.max(0, fours)).fill(4), ...Array(Math.max(0, twos)).fill(2)];
    try { await axios.put(`${API}/api/tm/lanes/${id}/tables`, { capacities: caps }, { headers }); await reloadFloor(); toast.success("Masalar güncellendi"); }
    catch { toast.error("Güncellenemedi"); }
  };


  const setLaneMembers = async (id, spec) => {
    try {
      const r = await axios.put(`${API}/api/tm/lanes/${id}/members`, { spec }, { headers });
      await reloadFloor();
      const d = r.data || {};
      if (d.created?.length) toast.success(`${d.count} masa bağlandı (${d.capacity} kişi) · yeni oluşturulan: ${d.created.join(", ")} — kapasiteyi masaya tıklayıp düzenleyin`);
      else if (d.missing?.length) toast.warning(`${d.count} masa bağlandı · bulunamayan: ${d.missing.join(", ")}`);
      else toast.success(`${d.count} masa bağlandı (${d.capacity} kişi)`);
    } catch { toast.error("Masa listesi kaydedilemedi"); }
  };

  const arrangeLane = async (id) => {
    try {
      const r = await axios.post(`${API}/api/tm/lanes/${id}/arrange`, arrangeOpt, { headers });
      await reloadFloor();
      toast.success(`${r.data?.count || 0} masa dizildi${r.data?.height ? " · bölge uzatıldı" : ""}`);
    } catch { toast.error("Masalar dizilemedi"); }
  };

  const boatFloors = floors.filter(f => f.boat_id === selectedBoat);
  const floorTables = tables.filter(t => t.floor_id === selectedFloor);
  const floorElements = elements.filter(e => e.floor_id === selectedFloor);
  const currentFloor = floors.find(f => f.id === selectedFloor);

  // ---- Zoom / Sığdır (ortak hook — Operasyon Panosu ile AYNI) ----
  const { zoom, viewportRef, zoomBy, resetZoom, fitToView } = useFloorFit(
    floorTables, floorElements, !!selectedFloor, `${selectedFloor}-${tables.length}-${elements.length}`
  );

  useEffect(() => {
    if (boatFloors.length > 0 && !boatFloors.find(f => f.id === selectedFloor)) {
      setSelectedFloor(boatFloors[0].id);
    }
  }, [selectedBoat, floors]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- CRUD Actions (optimistic — no loading flash) ----
  const addFloor = async () => {
    if (!newFloor.name.trim() || !selectedBoat) return;
    try {
      const res = await axios.post(`${API}/api/tm/floors`, {
        boat_id: selectedBoat, name: newFloor.name.trim(),
        floor_type: newFloor.floor_type, sort_order: boatFloors.length,
      }, { headers });
      const created = res.data?.floor;
      if (created) setFloors(prev => [...prev, created]);
      setNewFloor({ name: "", floor_type: "STANDART" });
      setAddFloorOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Kat eklenemedi"); }
  };

  const deleteFloor = async (id) => {
    if (!window.confirm("Bu katı ve tüm içeriğini silmek istediğinize emin misiniz?")) return;
    setFloors(prev => prev.filter(f => f.id !== id));
    setTables(prev => prev.filter(t => t.floor_id !== id));
    setElements(prev => prev.filter(e => e.floor_id !== id));
    if (selectedFloor === id) setSelectedFloor(null);
    try { await axios.delete(`${API}/api/tm/floors/${id}`, { headers }); } catch (e) { console.error("Kat silme hatası:", e); toast.error("Kat silinemedi"); }
  };

  const deleteTable = async (id) => {
    setTables(prev => prev.filter(t => t.id !== id));
    setSelected(null);
    try { await axios.delete(`${API}/api/tm/tables/${id}`, { headers }); } catch (e) { console.error("Masa silme hatası:", e); toast.error("Masa silinemedi"); }
  };

  const deleteElement = async (id) => {
    setElements(prev => prev.filter(e => e.id !== id));
    setSelected(null);
    try { await axios.delete(`${API}/api/tm/elements/${id}`, { headers }); } catch (e) { console.error("Öğe silme hatası:", e); toast.error("Öğe silinemedi"); }
  };

  const updateTableField = async (id, field, value) => {
    await axios.put(`${API}/api/tm/tables/${id}`, { [field]: value }, { headers });
    setTables(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const updateElementField = async (id, data) => {
    await axios.put(`${API}/api/tm/elements/${id}`, data, { headers });
    setElements(prev => prev.map(e => e.id === id ? { ...e, ...data } : e));
  };

  // ---- Canvas Click: Add item (optimistic) ----
  const handleCanvasClick = async (e) => {
    e.preventDefault();
    if (!toolMode || !selectedFloor) return;
    if (toolMode === "lane") return; // şerit çizim pointer olayları ile yönetilir
    const rect = canvasRef.current.getBoundingClientRect();
    const x = snapVal((e.clientX - rect.left) / zoom);
    const y = snapVal((e.clientY - rect.top) / zoom);

    if (toolMode === "table") {
      if (!newTableForm.number.trim()) { toast.error("Masa numarası girin"); return; }
      try {
        const res = await axios.post(`${API}/api/tm/tables`, {
          floor_id: selectedFloor, number: newTableForm.number.trim(),
          zone: newTableForm.zone, capacity: newTableForm.capacity,
          x: x - 40, y: y - 30, width: 100, height: 70,
        }, { headers });
        const created = res.data?.table;
        if (created) setTables(prev => [...prev, created]);
        // Auto-increment number
        const num = newTableForm.number.trim();
        const match = num.match(/^([A-Za-z]*)(\d+)$/);
        if (match) {
          setNewTableForm(p => ({ ...p, number: match[1] + String(parseInt(match[2]) + 1).padStart(match[2].length, '0') }));
        }
      } catch (err) { toast.error(err.response?.data?.detail || "Masa eklenemedi"); }
    } else {
      const def = getElementDef(toolMode);
      try {
        const res = await axios.post(`${API}/api/tm/elements`, {
          floor_id: selectedFloor, element_type: toolMode,
          label: newElementLabel || def.label,
          x: x - def.defaultW / 2, y: y - def.defaultH / 2,
          width: def.defaultW, height: def.defaultH,
        }, { headers });
        const created = res.data?.element;
        if (created) setElements(prev => [...prev, created]);
      } catch (err) { toast.error(err.response?.data?.detail || "Öğe eklenemedi"); }
    }
  };

  // ---- Akıcı sürükle & boyutlandır (pointer capture + doğrudan DOM, yeniden render yok) ----
  const dragState = useRef(null);
  const resizeState = useRef(null);

  const clampToCanvas = (x, y, w, h) => ({
    x: Math.max(0, Math.min(x, WLOGICAL - w)),
    y: Math.max(0, Math.min(y, HLOGICAL - h)),
  });

  const onItemPointerDown = (e, type, id) => {
    if (toolMode || e.button === 2) return;
    e.stopPropagation();
    const node = e.currentTarget;
    const coll = type === "table" ? tables : type === "element" ? elements : lanes;
    const item = coll.find(x => x.id === id);
    if (!item) return;
    setSelected({ type, id });
    const rect = canvasRef.current.getBoundingClientRect();
    try { node.setPointerCapture(e.pointerId); } catch { /* noop */ }
    dragState.current = {
      type, id, node,
      offsetX: (e.clientX - rect.left) / zoom - item.x,
      offsetY: (e.clientY - rect.top) / zoom - item.y,
      w: node.offsetWidth, h: node.offsetHeight,
      lastX: item.x, lastY: item.y, moved: false, raf: 0,
    };
  };

  const onItemPointerMove = (e) => {
    const d = dragState.current;
    if (!d) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let nx = snapVal((e.clientX - rect.left) / zoom - d.offsetX);
    let ny = snapVal((e.clientY - rect.top) / zoom - d.offsetY);
    const c = clampToCanvas(nx, ny, d.w, d.h);
    d.lastX = c.x; d.lastY = c.y; d.moved = true;
    if (!d.raf) {
      d.raf = requestAnimationFrame(() => {
        d.raf = 0;
        d.node.style.left = d.lastX + "px";
        d.node.style.top = d.lastY + "px";
      });
    }
  };

  const onItemPointerUp = (e) => {
    const d = dragState.current;
    if (!d) return;
    dragState.current = null;
    if (d.raf) cancelAnimationFrame(d.raf);
    try { d.node.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    if (!d.moved) return;
    const x = Math.round(d.lastX), y = Math.round(d.lastY);
    d.node.style.left = x + "px"; d.node.style.top = y + "px";
    if (d.type === "table") {
      setTables(prev => prev.map(t => t.id === d.id ? { ...t, x, y } : t));
      axios.put(`${API}/api/tm/tables/${d.id}/position`, { x, y }, { headers }).catch(() => {});
    } else if (d.type === "lane") {
      setLanes(prev => prev.map(l => l.id === d.id ? { ...l, x, y } : l));
      axios.put(`${API}/api/tm/lanes/${d.id}`, { x, y }, { headers }).then(() => reloadFloor()).catch(() => {});
    } else {
      setElements(prev => prev.map(el => el.id === d.id ? { ...el, x, y } : el));
      axios.put(`${API}/api/tm/elements/${d.id}`, { x, y }, { headers }).catch(() => {});
    }
  };

  const onResizePointerDown = (e, id, type = "element") => {
    e.stopPropagation(); e.preventDefault();
    const node = e.currentTarget.parentElement;
    const coll = type === "lane" ? lanes : elements;
    const item = coll.find(el => el.id === id);
    if (!item) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    resizeState.current = { id, type, node, handle: e.currentTarget, startX: e.clientX, startY: e.clientY, startW: item.width, startH: item.height, lastW: item.width, lastH: item.height, raf: 0 };
  };
  const onResizePointerMove = (e) => {
    const r = resizeState.current;
    if (!r) return;
    const minH = r.type === "lane" ? 90 : 16;
    const minW = r.type === "lane" ? 120 : 30;
    r.lastW = Math.max(minW, r.startW + (e.clientX - r.startX));
    r.lastH = Math.max(minH, r.startH + (e.clientY - r.startY));
    if (!r.raf) {
      r.raf = requestAnimationFrame(() => { r.raf = 0; r.node.style.width = r.lastW + "px"; r.node.style.height = r.lastH + "px"; });
    }
  };
  const onResizePointerUp = (e) => {
    const r = resizeState.current;
    if (!r) return;
    resizeState.current = null;
    if (r.raf) cancelAnimationFrame(r.raf);
    try { r.handle.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    const w = Math.round(r.lastW), h = Math.round(r.lastH);
    if (r.type === "lane") {
      setLanes(prev => prev.map(l => l.id === r.id ? { ...l, width: w, height: h } : l));
      axios.put(`${API}/api/tm/lanes/${r.id}`, { width: w, height: h }, { headers }).then(() => reloadFloor()).catch(() => {});
    } else {
      setElements(prev => prev.map(el => el.id === r.id ? { ...el, width: w, height: h } : el));
      axios.put(`${API}/api/tm/elements/${r.id}`, { width: w, height: h }, { headers }).catch(() => {});
    }
  };

  // ---- Şerit çizimi (rubber-band) ----
  const onCanvasPointerDown = (e) => {
    if (toolMode !== "lane" || !selectedFloor) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = (e.clientX - rect.left) / zoom;
    const sy = (e.clientY - rect.top) / zoom;
    drawRef.current = { sx, sy };
    setDrawRect({ x: sx, y: sy, w: 0, h: 0 });
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onCanvasPointerMove = (e) => {
    if (!drawRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = (e.clientX - rect.left) / zoom, cy = (e.clientY - rect.top) / zoom;
    const { sx, sy } = drawRef.current;
    setDrawRect({ x: Math.min(sx, cx), y: Math.min(sy, cy), w: Math.abs(cx - sx), h: Math.abs(cy - sy) });
  };
  const onCanvasPointerUp = (e) => {
    if (!drawRef.current) return;
    const r = drawRect;
    drawRef.current = null;
    setDrawRect(null);
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    if (!r || r.w < 60 || r.h < 60) { toast.error("Daha büyük bir alan çizin"); return; }
    addLaneRect(r);
    setToolMode(null);
  };


  const selectedItem = selected ? (selected.type === "table" ? tables.find(t => t.id === selected.id) : selected.type === "lane" ? lanes.find(l => l.id === selected.id) : elements.find(e => e.id === selected.id)) : null;

  const laneStats = (laneId) => {
    const lt = tables.filter(t => t.lane_id === laneId);
    const used = lt.reduce((s, t) => s + (t.capacity || 0), 0);
    const fours = lt.filter(t => (t.capacity || 0) >= 4).length;
    const twos = lt.filter(t => (t.capacity || 0) < 4).length;
    return { used, fours, twos, count: lt.length };
  };

  // ---- Bölge (renkli alan) renderer ----
  const renderLane = (l) => {
    const zc = ZONES.find(z => z.value === l.zone) || ZONES[3];
    const isSelected = selected?.type === "lane" && selected?.id === l.id;
    return (
      <div key={`lane-${l.id}`}
        onPointerDown={(e) => onItemPointerDown(e, "lane", l.id)}
        onPointerMove={onItemPointerMove}
        onPointerUp={onItemPointerUp}
        className={`absolute rounded-xl select-none ${toolMode ? "pointer-events-none" : "cursor-move"}`}
        style={{
          left: l.x, top: l.y, width: l.width, height: l.height, zIndex: 1,
          background: `linear-gradient(160deg, ${zc.color}22, ${zc.color}0a)`,
          border: `2px dashed ${zc.color}${isSelected ? "" : "aa"}`,
          boxShadow: isSelected ? `0 0 0 2px #38BDF8, 0 8px 24px rgba(0,0,0,0.4)` : "none",
          touchAction: "none",
        }}
        data-testid={`canvas-lane-${l.id}`}>
        <div className="absolute -top-3 left-2 pointer-events-none">
          <span className="px-2.5 py-0.5 rounded-md text-[11px] font-black uppercase tracking-wider shadow"
            style={{ background: zc.badgeBg, color: zc.badgeText }}>{l.name}</span>
        </div>
        {(() => {
          const st = laneStats(l.id);
          if (!l.dynamic && !st.count) return null;
          return (
            <div className="absolute -top-3 right-2 pointer-events-none flex gap-1">
              {st.count > 0 && (
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold shadow border ${l.capacity && st.used > l.capacity ? "bg-red-600 text-white border-red-400" : "bg-slate-800 text-slate-100 border-slate-600"}`} data-testid={`lane-members-${l.id}`}>
                  {st.count} masa · {l.capacity ? `${st.used}/${l.capacity}` : st.used} kişi
                </span>
              )}
              {l.dynamic && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold shadow flex items-center gap-1 bg-emerald-600 text-white">
                  <Users className="w-3 h-3" /> Dinamik · {l.capacity} kişi
                </span>
              )}
            </div>
          );
        })()}
        {isSelected && !toolMode && (
          <div onPointerDown={(e) => onResizePointerDown(e, l.id, "lane")}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            style={{ touchAction: "none" }}
            className="absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-sky-500 rounded-full cursor-se-resize border-2 border-slate-900 shadow z-50" />
        )}
      </div>
    );
  };

  // ---- Structural element renderer (dark) ----
  const renderElement = (el) => {
    const isSelected = selected?.type === "element" && selected?.id === el.id;
    const common = `absolute select-none ${toolMode ? "pointer-events-none" : "cursor-move"} ${isSelected ? "ring-2 ring-sky-400 ring-offset-1 ring-offset-slate-950 rounded" : ""}`;
    let inner = null;
    if (el.element_type === "sahne") {
      const fs = Math.max(11, Math.min(28, Math.min(el.width, el.height) / 4.5));
      const is = Math.max(16, fs * 1.5);
      inner = (
        <div className="w-full h-full relative flex items-center justify-center rounded-t-[28px] rounded-b-2xl border-2 border-orange-400/70 overflow-hidden"
          style={{ background: "linear-gradient(180deg, rgba(249,115,22,0.28), rgba(120,53,15,0.35))", boxShadow: "inset 0 2px 0 rgba(253,186,116,0.5), 0 0 28px rgba(249,115,22,0.28)" }}>
          {/* sahne ışıkları (footlights) */}
          <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-2 opacity-70">
            {Array.from({ length: Math.max(3, Math.round(el.width / 32)) }).map((_, i) => (
              <span key={i} className="w-1 h-1 rounded-full bg-orange-300" />
            ))}
          </div>
          <div className="text-center px-2">
            <Music className="text-orange-200 mx-auto mb-0.5 drop-shadow" style={{ width: is, height: is }} />
            <span className="font-black text-orange-100 tracking-[0.3em] uppercase leading-none" style={{ fontSize: fs }}>{el.label || "SAHNE"}</span>
          </div>
        </div>
      );
    } else if (el.element_type === "bar") {
      inner = (<div className="w-full h-full flex items-center justify-center rounded-lg border-2 border-amber-500/60 bg-amber-500/10 gap-1.5"><Wine className="w-4 h-4 text-amber-400" /><span className="text-xs font-bold text-amber-300">{el.label || "BAR"}</span></div>);
    } else if (el.element_type === "wc") {
      inner = (<div className="w-full h-full flex items-center justify-center rounded border border-slate-600 bg-slate-700/40 gap-1"><Bath className="w-3 h-3 text-slate-400" /><span className="text-[9px] font-bold text-slate-400">{el.label || "WC"}</span></div>);
    } else if (el.element_type === "plant") {
      inner = (<div className="w-full h-full flex items-center justify-center"><TreePalm className="w-5 h-5 text-emerald-400 drop-shadow" /></div>);
    } else if (el.element_type === "zone_label") {
      inner = (<div className="w-full h-full flex items-center justify-center"><span className="text-xs font-black tracking-[0.2em] uppercase text-sky-400/70">{el.label || "BÖLGE"}</span></div>);
    } else if (el.element_type === "border") {
      inner = (<div className="w-full border-t-2 border-dashed border-sky-500/30" style={{ marginTop: el.height / 2 }} />);
    } else if (el.element_type === "text") {
      inner = (<span className="text-[10px] text-slate-400 font-medium">{el.label}</span>);
    }
    return (
      <div key={`el-${el.id}`}
        onPointerDown={(e) => onItemPointerDown(e, "element", el.id)}
        onPointerMove={onItemPointerMove}
        onPointerUp={onItemPointerUp}
        className={common}
        style={{ left: el.x, top: el.y, width: el.width, height: el.height, zIndex: el.element_type === "border" ? 1 : 5, transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined, transformOrigin: "center", touchAction: "none" }}
        data-testid={`canvas-element-${el.id}`}>
        {inner}
        {isSelected && !toolMode && (
          <div onPointerDown={(e) => onResizePointerDown(e, el.id)}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            style={{ touchAction: "none" }}
            className="absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-sky-500 rounded-full cursor-se-resize border-2 border-slate-900 shadow z-50" />
        )}
      </div>
    );
  };

  // ---- Gerçekçi masa görünümü (ortak RealTable bileşeni — Operasyon Panosu ile aynı) ----
  const renderTable = (t) => {
    const zs = getZoneStyle(t.zone);
    const isSelected = selected?.type === "table" && selected?.id === t.id;
    const cap = t.capacity ?? 0; // 0 = kapasite henüz verilmedi ("x" görünür, toplamı etkilemez)
    return (
      <div key={`t-${t.id}`}
        onPointerDown={(e) => onItemPointerDown(e, "table", t.id)}
        onPointerMove={onItemPointerMove}
        onPointerUp={onItemPointerUp}
        className={`absolute flex flex-col items-center select-none group ${toolMode ? "pointer-events-none" : "cursor-move"} ${isSelected ? "z-40" : "z-10"}`}
        style={{ left: t.x, top: t.y, transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined, transformOrigin: "center", touchAction: "none" }}
        data-testid={`canvas-table-${t.id}`}>
        <RealTable
          rotation={t.rotation || 0}
          number={t.number}
          capacity={cap}
          edgeColor={zs.color}
          bodyBg={`linear-gradient(160deg, ${zs.color}26, #131B2E 70%)`}
          bodyBorderStyle="solid"
          glow={isSelected ? `0 0 0 3px #38BDF8, 0 6px 18px rgba(0,0,0,0.5)` : `0 4px 12px rgba(0,0,0,0.45)`}
          numberSize={19}
          sublabel={cap ? `${cap} kişi` : "x"}
          sublabelColor={zs.color}
          fillAllChairs={true}
          chairFilledColor={zs.color}
          chairFilledOpacity={0.9}
          chairBorder={false}
        />
      </div>
    );
  };

  if (loading) return <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-slate-500 text-sm">Yükleniyor...</div>;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="table-schema-editor">
      {/* === HEADER === */}
      <div className="bg-slate-900/90 backdrop-blur-md border-b border-slate-800 sticky top-0 z-40">
        <div className="px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-900/40"><Anchor className="w-5 h-5 text-white" /></div>
            <div>
              <div className="text-[10px] font-bold text-amber-400 uppercase tracking-[0.2em]">Lüfer · Masa Düzeni</div>
              <h1 className="text-xl font-bold tracking-tight text-white -mt-0.5">Şema Editörü</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/table-layout" className="px-4 py-1.5 bg-slate-800 text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-1.5" data-testid="tab-operations"><ArrowLeft className="w-3.5 h-3.5" /> Operasyon</Link>
            <span className="px-4 py-1.5 bg-sky-500 text-white text-sm font-bold rounded-lg shadow-lg shadow-sky-900/40" data-testid="tab-schema">Şema Editörü</span>
          </div>
          <div className="text-xs text-slate-500 font-medium min-w-[160px] text-right">
            {currentFloor ? `${boats.find(b => b.id === selectedBoat)?.name} / ${currentFloor.name}` : "Kat seçin"}
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-65px)]">
        {/* LEFT: Boat + Floor + Tool palette */}
        <div className="w-60 bg-slate-900 border-r border-slate-800 flex flex-col shrink-0 overflow-y-auto">
          {/* Boats */}
          <div className="p-3 border-b border-slate-800">
            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Tekne</label>
            <div className="mt-1.5 space-y-1">
              {boats.map(b => (
                <button type="button" key={b.id} onClick={() => setSelectedBoat(b.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedBoat === b.id ? "bg-sky-500/15 text-sky-300 border-sky-500/60 shadow-[0_0_12px_rgba(56,189,248,0.2)]" : "text-slate-400 border-slate-700 hover:border-slate-600"}`}
                  data-testid={`schema-boat-${b.id}`}>
                  <Ship className="w-3.5 h-3.5" /> {b.name}
                </button>
              ))}
            </div>
          </div>

          {/* Floors */}
          <div className="p-3 border-b border-slate-800">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Katlar</label>
              <button type="button" onClick={() => setAddFloorOpen(!addFloorOpen)} className="text-amber-400 hover:text-amber-300" data-testid="add-floor-toggle"><Plus className="w-4 h-4" /></button>
            </div>
            {addFloorOpen && (
              <div className="mb-2 p-2 bg-slate-800/60 rounded-lg border border-slate-700 space-y-1.5">
                <input value={newFloor.name} onChange={e => setNewFloor(p => ({ ...p, name: e.target.value }))}
                  placeholder="Kat adı" className="w-full px-2 py-1 text-xs bg-slate-900 border border-slate-700 rounded text-slate-100 placeholder-slate-600 focus:border-sky-500 outline-none" data-testid="new-floor-name" />
                <select value={newFloor.floor_type} onChange={e => setNewFloor(p => ({ ...p, floor_type: e.target.value }))}
                  className="w-full px-2 py-1 text-xs bg-slate-900 border border-slate-700 rounded text-slate-100 outline-none">
                  {FLOOR_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
                <button type="button" onClick={addFloor} className="w-full py-1 bg-amber-500 text-slate-900 text-xs font-bold rounded hover:bg-amber-400 transition-colors" data-testid="add-floor-submit">Ekle</button>
              </div>
            )}
            {boatFloors.map(f => (
              <div key={f.id} onClick={() => setSelectedFloor(f.id)}
                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg mb-1 cursor-pointer text-xs border transition-all ${selectedFloor === f.id ? (f.floor_type === "VIP" ? "bg-amber-500/15 border-amber-500/60 font-semibold text-amber-300" : "bg-slate-700 border-slate-500 font-semibold text-slate-100") : "text-slate-400 border-slate-700 hover:border-slate-600"}`}
                data-testid={`schema-floor-${f.id}`}>
                <div className="flex items-center gap-1.5">
                  {f.floor_type === "VIP" ? <Crown className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
                  <span>{f.name}</span>
                  {f.floor_type === "VIP" && <span className="text-[8px] font-bold px-1 rounded bg-amber-500/20 text-amber-300">VIP</span>}
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); deleteFloor(f.id); }} className="text-slate-600 hover:text-red-400" data-testid={`delete-floor-${f.id}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>


          {/* Tool Palette */}
          {selectedFloor && (
            <div className="p-3 flex-1">
              <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Araçlar</label>
              <p className="text-[9px] text-slate-600 mb-2">Seç, sonra canvas'a tıkla</p>

              <button type="button" onClick={() => setToolMode(null)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs mb-1.5 border transition-all ${!toolMode ? "bg-sky-500/15 text-sky-300 border-sky-500/60 font-semibold" : "text-slate-400 border-transparent hover:bg-slate-800"}`}
                data-testid="tool-select">
                <Move className="w-3.5 h-3.5" /> Seç / Taşı
              </button>

              {/* Table tool */}
              <div className={`mb-2 rounded-lg border ${toolMode === "table" ? "border-amber-500/50 bg-amber-500/5" : "border-transparent"}`}>
                <button type="button" onClick={() => setToolMode("table")}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${toolMode === "table" ? "text-amber-300 font-semibold" : "text-slate-400 hover:bg-slate-800"}`}
                  data-testid="tool-table">
                  <Square className="w-3.5 h-3.5" /> Masa Ekle
                </button>
                {toolMode === "table" && (
                  <div className="px-2.5 pb-2 space-y-1.5">
                    <input value={newTableForm.number} onChange={e => setNewTableForm(p => ({ ...p, number: e.target.value }))}
                      placeholder="No (C01)" className="w-full px-2 py-1 text-[11px] bg-slate-900 border border-slate-700 rounded text-slate-100 placeholder-slate-600 focus:border-amber-500 outline-none" data-testid="new-table-number" />
                    <div className="flex gap-1.5">
                      <select value={newTableForm.zone} onChange={e => setNewTableForm(p => ({ ...p, zone: e.target.value }))}
                        className="flex-1 px-1.5 py-1 text-[11px] bg-slate-900 border border-slate-700 rounded text-slate-100 outline-none" data-testid="new-table-zone">
                        {ZONES.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                      </select>
                      <input type="number" min="1" max="20" value={newTableForm.capacity} onChange={e => setNewTableForm(p => ({ ...p, capacity: parseInt(e.target.value) || 4 }))}
                        className="w-12 px-1 py-1 text-[11px] bg-slate-900 border border-slate-700 rounded text-slate-100 text-center outline-none" data-testid="new-table-capacity" />
                    </div>
                    <p className="text-[9px] text-slate-600">Maks. kapasiteyi girin — masa buna göre büyür, paylaşımda kalan yer buna göre hesaplanır.</p>
                  </div>
                )}
              </div>

              {/* Bölge (renkli alan) çizim aracı */}
              <div className={`mb-2 rounded-lg border ${toolMode === "lane" ? "border-emerald-500/50 bg-emerald-500/5" : "border-transparent"}`}>
                <button type="button" onClick={() => setToolMode(toolMode === "lane" ? null : "lane")}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${toolMode === "lane" ? "text-emerald-300 font-semibold" : "text-slate-400 hover:bg-slate-800"}`}
                  data-testid="tool-lane">
                  <BoxSelect className="w-3.5 h-3.5" /> Bölge Ekle
                </button>
                {toolMode === "lane" && (
                  <div className="px-2.5 pb-2 space-y-1.5">
                    <input value={newLane.name} onChange={e => setNewLane(p => ({ ...p, name: e.target.value }))}
                      placeholder="Bölge adı (örn. Cam Kenarı)" className="w-full px-2 py-1 text-[11px] bg-slate-900 border border-slate-700 rounded text-slate-100 placeholder-slate-600 outline-none" data-testid="new-lane-name" />
                    <select value={newLane.zone} onChange={e => setNewLane(p => ({ ...p, zone: e.target.value }))}
                      className="w-full px-1.5 py-1 text-[11px] bg-slate-900 border border-slate-700 rounded text-slate-100 outline-none" data-testid="new-lane-zone">
                      {ZONES.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                    </select>
                    <label className="flex items-center gap-1.5 text-[10px] text-slate-300 cursor-pointer">
                      <input type="checkbox" checked={newLane.dynamic} onChange={e => setNewLane(p => ({ ...p, dynamic: e.target.checked }))} data-testid="new-lane-dynamic" />
                      Dinamik (rezervasyona göre masa oluştur)
                    </label>
                    {newLane.dynamic && (
                      <div className="flex items-center gap-1.5">
                        <label className="text-[9px] text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Kapasite</label>
                        <input type="number" min="2" max="300" value={newLane.capacity} onChange={e => setNewLane(p => ({ ...p, capacity: parseInt(e.target.value) || 0 }))}
                          className="w-16 px-1 py-0.5 text-[10px] bg-slate-900 border border-slate-700 rounded text-center text-slate-100 outline-none" data-testid="new-lane-capacity" />
                        <span className="text-[9px] text-slate-600">kişi</span>
                      </div>
                    )}
                    <p className="text-[9px] text-emerald-400/80">{newLane.dynamic ? "Operasyonda bu bölgeye rezervasyon koydukça masa otomatik oluşur." : "Canvas'a bir dikdörtgen çizin — renkli bölge oluşur."}</p>
                  </div>
                )}
              </div>


              {/* Structural elements */}
              <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mt-2 block">Yapısal</label>
              {ELEMENT_TYPES.map(et => (
                <div key={et.type} className={`rounded-lg border mb-0.5 ${toolMode === et.type ? "border-sky-500/50 bg-sky-500/5" : "border-transparent"}`}>
                  <button type="button" onClick={() => setToolMode(toolMode === et.type ? null : et.type)}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-lg ${toolMode === et.type ? "text-sky-300 font-semibold" : "text-slate-400 hover:bg-slate-800"}`}
                    data-testid={`tool-${et.type}`}>
                    <et.icon className="w-3.5 h-3.5" /> {et.label}
                  </button>
                  {toolMode === et.type && (et.type === "zone_label" || et.type === "text") && (
                    <div className="px-2.5 pb-1.5">
                      <input value={newElementLabel} onChange={e => setNewElementLabel(e.target.value)}
                        placeholder="Metin..." className="w-full px-2 py-1 text-[11px] bg-slate-900 border border-slate-700 rounded text-slate-100 placeholder-slate-600 outline-none" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CENTER: Canvas */}
        <div className="flex-1 relative overflow-hidden">
          {/* Zoom / Sığdır / Izgara araç çubuğu (sabit, ölçeklenmez) */}
          {selectedFloor && currentFloor && (
            <div className="absolute top-3 right-3 z-30 flex items-center gap-1 bg-slate-900/90 backdrop-blur-md border border-slate-800 rounded-lg px-1.5 py-1 shadow-xl" data-testid="canvas-toolbar">
              <button type="button" onClick={() => setSnap(s => !s)} title="Izgaraya hizala"
                className={`px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 transition-colors ${snap ? "bg-sky-500/20 text-sky-300" : "text-slate-400 hover:bg-slate-800"}`}
                data-testid="toggle-snap"><Grid3x3 className="w-3.5 h-3.5" /> Izgara</button>
              <div className="w-px h-4 bg-slate-700 mx-0.5" />
              <button type="button" onClick={() => zoomBy(-0.1)} className="w-6 h-6 rounded text-slate-300 hover:bg-slate-800 flex items-center justify-center" data-testid="zoom-out"><Minus className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={resetZoom} className="text-[10px] font-mono font-bold text-slate-300 w-10 hover:text-sky-300" data-testid="zoom-reset">{Math.round(zoom * 100)}%</button>
              <button type="button" onClick={() => zoomBy(0.1)} className="w-6 h-6 rounded text-slate-300 hover:bg-slate-800 flex items-center justify-center" data-testid="zoom-in"><Plus className="w-3.5 h-3.5" /></button>
              <div className="w-px h-4 bg-slate-700 mx-0.5" />
              <button type="button" onClick={fitToView} title="Tümünü sığdır"
                className="px-2 py-1 rounded text-[10px] font-bold text-amber-300 hover:bg-amber-500/10 flex items-center gap-1" data-testid="fit-view"><Maximize2 className="w-3.5 h-3.5" /> Sığdır</button>
            </div>
          )}
          {selectedFloor && currentFloor ? (
            <div ref={viewportRef} className="w-full h-full overflow-auto p-4">
              <div style={{ width: WLOGICAL * zoom, height: HLOGICAL * zoom }}>
                <div ref={canvasRef} onClick={handleCanvasClick}
                  onPointerDown={onCanvasPointerDown} onPointerMove={onCanvasPointerMove} onPointerUp={onCanvasPointerUp}
                  className={`relative rounded-xl border border-slate-800 overflow-hidden ${toolMode ? "cursor-crosshair" : "cursor-default"}`}
                  style={{
                    width: WLOGICAL, height: HLOGICAL,
                    transform: `scale(${zoom})`, transformOrigin: "0 0",
                    backgroundColor: "#0f1622",
                    backgroundImage: snap ? `linear-gradient(#ffffff08 1px, transparent 1px), linear-gradient(90deg, #ffffff08 1px, transparent 1px)` : "none",
                    backgroundSize: snap ? `${GRID}px ${GRID}px, ${GRID}px ${GRID}px` : "100% 100%",
                  }}
                  data-testid="schema-canvas">

                  {/* Floor label */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 text-xs font-bold text-slate-500 uppercase tracking-[0.2em] pointer-events-none flex items-center gap-2">
                    {currentFloor.floor_type === "VIP" && <Crown className="w-3.5 h-3.5 text-amber-400" />}{currentFloor.name}
                  </div>

                  {/* Şerit dikdörtgenleri (arka planda, masalar üstte) */}
                  {lanes.map(l => renderLane(l))}

                  {floorElements.map(el => renderElement(el))}
                  {floorTables.map(t => renderTable(t))}

                  {/* Çizim önizleme (rubber-band) */}
                  {drawRect && (
                    <div className="absolute rounded-xl pointer-events-none border-2 border-dashed border-sky-400 bg-sky-400/10"
                      style={{ left: drawRect.x, top: drawRect.y, width: drawRect.w, height: drawRect.h, zIndex: 50 }} />
                  )}

                  {floorTables.length === 0 && floorElements.length === 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-600 pointer-events-none">
                      <Square className="w-12 h-12 mb-2 text-slate-700" />
                      <p className="text-sm text-slate-400">Soldaki araç paletten seçerek canvas'a tıklayın</p>
                    </div>
                  )}

                  {/* Legend */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20 bg-slate-900/90 backdrop-blur-md px-4 py-1.5 rounded-full border border-slate-800 shadow-xl pointer-events-none">
                    {ZONES.map(z => (
                      <div key={z.value} className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: z.color }} />
                        <span className="text-[10px] font-medium text-slate-400">{z.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-500">
              <div className="text-center">
                <Ship className="w-16 h-16 mx-auto mb-3 text-slate-700" />
                <p className="text-sm">{boats.length === 0 ? "Tekne bulunamadı" : boatFloors.length === 0 ? "Bu tekneye kat ekleyin" : "Bir kat seçin"}</p>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Properties panel */}
        <div className="w-56 bg-slate-900 border-l border-slate-800 flex flex-col shrink-0 overflow-y-auto">
          <div className="p-3 border-b border-slate-800">
            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Özellikler</label>
          </div>
          {selectedItem ? (
            <div className="p-3 space-y-2.5">
              {selected.type === "lane" && (
                <>
                  <div className="flex items-center gap-1.5 text-emerald-300 font-bold text-xs uppercase tracking-wider"><BoxSelect className="w-4 h-4" /> Bölge</div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Bölge Adı</label>
                    <input value={selectedItem.name || ""} onChange={e => setLanes(prev => prev.map(l => l.id === selected.id ? { ...l, name: e.target.value } : l))}
                      onBlur={e => updateLane(selected.id, { name: e.target.value })}
                      className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 focus:border-emerald-500 outline-none" data-testid="prop-lane-name" />
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Renk / Tür</label>
                    <select value={selectedItem.zone || "standart"} onChange={e => updateLane(selected.id, { zone: e.target.value })}
                      className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 outline-none" data-testid="prop-lane-zone">
                      {ZONES.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                    </select>
                    <div className="flex gap-1.5 mt-1.5">
                      {ZONES.map(z => (
                        <button key={z.value} type="button" onClick={() => updateLane(selected.id, { zone: z.value })}
                          title={z.label}
                          className={`w-6 h-6 rounded-full border-2 ${selectedItem.zone === z.value ? "border-white" : "border-transparent"}`}
                          style={{ background: z.color }} data-testid={`prop-lane-color-${z.value}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-[9px] text-slate-600">Bölgeyi taşımak için sürükle, boyutlandırmak için sağ-alt köşeyi çek.</p>
                  <div className="pt-1 border-t border-slate-800 space-y-1.5">
                    <label className="text-[9px] text-slate-500 block">Bölgenin masaları (numara listesi)</label>
                    <input value={selectedItem.table_spec ?? ""} placeholder="örn. 14-22, 30, ALT1"
                      onChange={e => setLanes(prev => prev.map(l => l.id === selected.id ? { ...l, table_spec: e.target.value } : l))}
                      onBlur={e => setLaneMembers(selected.id, e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 focus:border-emerald-500 outline-none" data-testid="prop-lane-table-spec" />
                    {(() => { const st = laneStats(selected.id); const cap = selectedItem.capacity || 0; const rem = cap - st.used; return (
                      <div className="space-y-1">
                        <div className="text-[10px] text-slate-400" data-testid="prop-lane-members-info">
                          {st.count ? <>Bu bölgede <b className="text-slate-100">{st.count} masa</b> · masalara dağıtılan <b className="text-slate-100">{st.used} kişi</b></> : "Henüz masa bağlı değil"}
                        </div>
                        {cap > 0 && st.count > 0 && (
                          <div className={`text-[11px] font-bold ${rem < 0 ? "text-red-400" : rem === 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="prop-lane-remaining">
                            {rem < 0 ? `Bölge kapasitesi ${-rem} kişi AŞILDI (${st.used}/${cap})` : `Kalan ${rem} kişi (${st.used}/${cap})`}
                          </div>
                        )}
                        {st.count > 0 && (
                          <div className="flex items-center gap-1 flex-wrap pt-0.5" data-testid="prop-lane-arrange-opts">
                            <select value={arrangeOpt.mode} onChange={e => setArrangeOpt(p => ({ ...p, mode: e.target.value }))}
                              className="px-1 py-0.5 text-[10px] bg-slate-900 border border-slate-700 rounded text-slate-100 outline-none" data-testid="prop-lane-arrange-mode">
                              <option value="auto">Sıralı (bölge yönüne göre)</option>
                              <option value="column">Dikey tek sıra</option>
                              <option value="row">Yatay tek sıra</option>
                              <option value="grid">Izgara (sığdıkça)</option>
                            </select>
                            <label className="flex items-center gap-1 text-[10px] text-slate-300 cursor-pointer">
                              <input type="checkbox" checked={arrangeOpt.rotate} onChange={e => setArrangeOpt(p => ({ ...p, rotate: e.target.checked }))} data-testid="prop-lane-arrange-rotate" /> 90° çevir
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-slate-300 cursor-pointer" title="Numaralar sondan başa dizilir (aşağıdan yukarı / sağdan sola)">
                              <input type="checkbox" checked={arrangeOpt.reverse} onChange={e => setArrangeOpt(p => ({ ...p, reverse: e.target.checked }))} data-testid="prop-lane-arrange-reverse" /> Ters sıra
                            </label>
                            <button type="button" onClick={() => arrangeLane(selected.id)}
                              className="px-2 py-0.5 text-[10px] font-semibold rounded border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition-colors" data-testid="prop-lane-arrange">Masaları Diz</button>
                          </div>
                        )}
                      </div>
                    ); })()}
                    <div className="flex items-center gap-1.5">
                      <label className="text-[9px] text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Bölge kapasitesi</label>
                      <input type="number" min="0" max="500" value={selectedItem.capacity || 0}
                        onChange={e => setLanes(prev => prev.map(l => l.id === selected.id ? { ...l, capacity: parseInt(e.target.value) || 0 } : l))}
                        onBlur={e => updateLane(selected.id, { capacity: parseInt(e.target.value) || 0 })}
                        className="w-16 px-1 py-0.5 text-[11px] bg-slate-900 border border-slate-700 rounded text-center text-slate-100 outline-none" data-testid="prop-lane-capacity" />
                      <span className="text-[9px] text-slate-600">kişi (0 = sınırsız)</span>
                    </div>
                    <div className="pt-1 space-y-1">
                      <label className="text-[9px] text-slate-500 block">Masa kontenjanı (bölgede en çok kaç masa) · boş = sınırsız, 0 = yok</label>
                      {[["cam_kenari", "Cam Kenarı"], ["sahne_onu", "Sahne Önü"], ["vip_lounge", "VIP Lounge"]].map(([k, lbl]) => (
                        <div key={k} className="flex items-center gap-1.5">
                          <span className="w-20 text-[10px] text-slate-300">{lbl}</span>
                          <input type="number" min="0" max="200" placeholder="∞"
                            value={selectedItem.quotas?.[k] ?? ""}
                            onChange={e => { const v = e.target.value; setLanes(prev => prev.map(l => l.id === selected.id ? { ...l, quotas: { ...(l.quotas || {}), [k]: v === "" ? null : parseInt(v) } } : l)); }}
                            onBlur={() => updateLane(selected.id, { quotas: Object.fromEntries(Object.entries(selectedItem.quotas || {}).filter(([, v]) => v !== null && v !== "")) })}
                            className="w-14 px-1 py-0.5 text-[11px] bg-slate-900 border border-slate-700 rounded text-center text-slate-100 outline-none" data-testid={`prop-lane-quota-${k}`} />
                          <span className="text-[9px] text-slate-600">masa</span>
                        </div>
                      ))}
                    </div>
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-200 cursor-pointer">
                      <input type="checkbox" checked={!!selectedItem.dynamic} onChange={e => updateLane(selected.id, { dynamic: e.target.checked })} data-testid="prop-lane-dynamic" />
                      Dinamik: sabit masalar dolunca rezervasyona göre masa oluştur
                    </label>
                  </div>
                  <div className="text-[9px] text-slate-600">{Math.round(selectedItem.width)}×{Math.round(selectedItem.height)}</div>
                  <button type="button" onClick={() => deleteLane(selected.id)} className="w-full py-1.5 bg-red-500/10 text-red-400 text-xs font-semibold rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-colors" data-testid="prop-delete-lane">Bölgeyi Sil</button>
                </>
              )}
              {selected.type === "table" && (
                <>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Numara</label>
                    <input value={selectedItem.number || ""} onChange={e => updateTableField(selected.id, "number", e.target.value)}
                      className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 focus:border-sky-500 outline-none" data-testid="prop-table-number" />
                  </div>
                  <div className="text-[10px] text-slate-400" data-testid="prop-table-lane">
                    Bölge: <b className="text-slate-100">{lanes.find(l => l.id === selectedItem.lane_id)?.name || "—"}</b>
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Renk / Tür</label>
                    <select value={selectedItem.zone || "standart"} onChange={e => updateTableField(selected.id, "zone", e.target.value)}
                      className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 outline-none" data-testid="prop-table-zone">
                      {ZONES.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Maks. Kapasite (kişi)</label>
                    <input type="number" min="0" max="40" value={selectedItem.capacity ?? 0} onChange={e => updateTableField(selected.id, "capacity", parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 outline-none" data-testid="prop-table-capacity" />
                    <p className="text-[9px] text-slate-600 mt-0.5">Masaya oturabilecek EN FAZLA kişi. Operasyon panosunda birden fazla rezervasyon bu sayıya kadar paylaşabilir.</p>
                    {selectedItem.lane_id && (() => { const ln = lanes.find(l => l.id === selectedItem.lane_id); if (!ln || !ln.capacity) return null; const st = laneStats(ln.id); const rem = ln.capacity - st.used; return (
                      <p className={`text-[10px] mt-1 font-semibold ${rem < 0 ? "text-red-400" : "text-emerald-300"}`} data-testid="prop-table-lane-remaining">
                        {ln.name}: {st.used}/{ln.capacity} kişi dağıtıldı · {rem < 0 ? `${-rem} kişi AŞIM` : `kalan ${rem} kişi`}
                      </p>
                    ); })()}
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Döndürme</label>
                    <div className="flex items-center gap-1">
                      <input type="range" min="-180" max="180" step="5" value={selectedItem.rotation || 0}
                        onChange={e => updateTableField(selected.id, "rotation", parseFloat(e.target.value))}
                        className="flex-1 h-1.5 accent-sky-500" />
                      <span className="text-[10px] font-mono text-slate-400 w-8 text-right">{selectedItem.rotation || 0}°</span>
                    </div>
                    <div className="flex gap-1 mt-1">
                      {[0, 45, 90, -45, -90].map(deg => (
                        <button type="button" key={deg} onClick={() => updateTableField(selected.id, "rotation", deg)}
                          className={`text-[8px] px-1.5 py-0.5 rounded border ${(selectedItem.rotation || 0) === deg ? "bg-sky-500/20 border-sky-500/60 text-sky-300" : "border-slate-700 text-slate-500 hover:border-slate-600"}`}>
                          {deg}°
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 text-[9px] text-slate-600">
                    <span>x: {Math.round(selectedItem.x)}</span>
                    <span>y: {Math.round(selectedItem.y)}</span>
                  </div>
                  <button type="button" onClick={() => deleteTable(selected.id)} className="w-full py-1.5 bg-red-500/10 text-red-400 text-xs font-semibold rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-colors" data-testid="prop-delete-table">Masayı Sil</button>
                </>
              )}
              {selected.type === "element" && (
                <>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Tip</label>
                    <span className="text-xs font-medium text-slate-200">{getElementDef(selectedItem.element_type).label}</span>
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Etiket</label>
                    <input value={selectedItem.label || ""} onChange={e => updateElementField(selected.id, { label: e.target.value })}
                      className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 focus:border-sky-500 outline-none" data-testid="prop-element-label" />
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Boyut (En × Boy)</label>
                    <div className="flex items-center gap-1.5">
                      <input type="number" min="30" max="1200" value={Math.round(selectedItem.width || 0)}
                        onChange={e => updateElementField(selected.id, { width: parseInt(e.target.value) || 30 })}
                        className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 outline-none" data-testid="prop-element-width" />
                      <span className="text-slate-600 text-xs">×</span>
                      <input type="number" min="16" max="800" value={Math.round(selectedItem.height || 0)}
                        onChange={e => updateElementField(selected.id, { height: parseInt(e.target.value) || 16 })}
                        className="w-full px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-100 outline-none" data-testid="prop-element-height" />
                    </div>
                    <div className="flex gap-1 mt-1">
                      {[[170,130],[240,160],[320,180],[420,220]].map(([w,h]) => (
                        <button type="button" key={w} onClick={() => updateElementField(selected.id, { width: w, height: h })}
                          className="text-[8px] px-1.5 py-0.5 rounded border border-slate-700 text-slate-400 hover:border-sky-500/60 hover:text-sky-300">{w}×{h}</button>
                      ))}
                    </div>
                    <p className="text-[9px] text-slate-600 mt-0.5">Sağ-alt köşeden sürükleyerek de boyutlandırabilirsiniz.</p>
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-500 block mb-0.5">Döndürme</label>
                    <div className="flex items-center gap-1">
                      <input type="range" min="-180" max="180" step="5" value={selectedItem.rotation || 0}
                        onChange={e => updateElementField(selected.id, { rotation: parseFloat(e.target.value) })}
                        className="flex-1 h-1.5 accent-sky-500" />
                      <span className="text-[10px] font-mono text-slate-400 w-8 text-right">{selectedItem.rotation || 0}°</span>
                    </div>
                    <div className="flex gap-1 mt-1">
                      {[0, 45, 90, -45, -90].map(deg => (
                        <button type="button" key={deg} onClick={() => updateElementField(selected.id, { rotation: deg })}
                          className={`text-[8px] px-1.5 py-0.5 rounded border ${(selectedItem.rotation || 0) === deg ? "bg-sky-500/20 border-sky-500/60 text-sky-300" : "border-slate-700 text-slate-500 hover:border-slate-600"}`}>
                          {deg}°
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 text-[9px] text-slate-600">
                    <span>x: {Math.round(selectedItem.x)}</span>
                    <span>y: {Math.round(selectedItem.y)}</span>
                    <span>{Math.round(selectedItem.width)}x{Math.round(selectedItem.height)}</span>
                  </div>
                  <button type="button" onClick={() => deleteElement(selected.id)} className="w-full py-1.5 bg-red-500/10 text-red-400 text-xs font-semibold rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-colors" data-testid="prop-delete-element">Öğeyi Sil</button>
                </>
              )}
            </div>
          ) : (
            <div className="p-3 text-[10px] text-slate-500 text-center">
              Bir eleman seçin veya soldaki araçlardan biriyle canvas'a tıklayın
            </div>
          )}

          {/* Summary */}
          <div className="mt-auto p-3 border-t border-slate-800 bg-slate-950/50">
            <div className="text-[10px] text-slate-500 space-y-1">
              <div className="flex justify-between"><span>Masalar</span><span className="font-bold text-slate-300 font-mono">{floorTables.length}</span></div>
              <div className="flex justify-between"><span>Koltuk (toplam)</span><span className="font-bold text-slate-300 font-mono">{floorTables.reduce((s, t) => s + (t.capacity || 0), 0)}</span></div>
              <div className="flex justify-between"><span>Elemanlar</span><span className="font-bold text-slate-300 font-mono">{floorElements.length}</span></div>
              {ZONES.map(z => {
                const cnt = floorTables.filter(t => t.zone === z.value).length;
                if (!cnt) return null;
                return <div key={z.value} className="flex justify-between"><span style={{ color: z.color }}>{z.label}</span><span className="font-bold font-mono" style={{ color: z.color }}>{cnt}</span></div>;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
